import { RouteComponentProps, useLocation } from '@gatsbyjs/reach-router'
import { Fragment, useEffect, useState } from 'react';
import MutualFundApiServices from '../../Services/MutualFundServices';
import NomineeMasterTable from '../../Models/NomineeMasterTable';
import { CircularProgress, Divider } from '@mui/material';
import moment from 'moment';
import { useAPForm, getIt, handleErrorMessage, APColumn, APSizedBox, APSpacing, APImage, APScrollView, APForm, APText, APFormFieldText, APFormFieldDropdown, APFormFieldCheckbox, REGEX, APRow, APFormSubmit, APCenter } from 'ap-components';
import { NomineeUpdateRequest, NomineeDetailsModel, NomineeUCCModel } from '../../Models/NomineeDeclarationModel';
import APSectionMessage from 'ap-components/src/elements/APSectionMessage';

function getIdentityPlaceholder(identityType: string): string {
    switch (identityType) {
        case '1': return "Nominee's PAN Number";
        case '2': return "Nominee's last 4 digits of Aadhaar";
        case '3': return "Nominee's Driving License Number";
        case '4': return "Nominee's Passport Number ";
        default: return "Nominee's Identity Number";
    }
}

export default function UpdateNomineeDetails(_props: RouteComponentProps) {
    const location = useLocation()
    const token = new URLSearchParams(location.search).get("tkn")
    const [clientDetails, setClientDetails] = useState<NomineeDetailsModel>()

    useEffect(() => {
        getDetails();
    }, [token]);

    async function getDetails() {
        if (!token) {
            return
        }

        try {
            const res = await getIt(MutualFundApiServices).getNomineeDetails(token);
            setClientDetails(res)
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    if (!clientDetails) {
        return (
            <APColumn mainAxisSize='max' crossAxisAlignment='stretch'>
                <APCenter>
                    <CircularProgress />
                </APCenter>
            </APColumn>
        )
    }

    if (!token) {
        return (
            <APColumn mainAxisSize='max' crossAxisAlignment='stretch'>
                <APCenter>
                    <APText> Unauthorized Access </APText>
                </APCenter>
            </APColumn>
        )
    }

    return <NomineeForm data={clientDetails} token={token} />
}

function NomineeForm(props: { data: NomineeDetailsModel, token: string }) {

    let { name: clientName, nominee } = props.data

    if (!nominee) {
        nominee = new NomineeUCCModel()
    }

    const control = useAPForm()
    const [form] = useState<NomineeUpdateRequest>({
        showInSoa: nominee.showNomineeInSoa ?? false,
        name: nominee.nominee ?? "",
        relationCode: nominee.nomineeRelationCode ?? "",
        dob: moment(nominee.nomineeDOB, "DD/MM/YYYY", true).isValid() ? moment(nominee.nomineeDOB, "DD/MM/YYYY", true).format("YYYY-MM-DD") : "",
        guardianName: nominee.nomineeGuardian ?? "",
        identificationType: nominee.nomineeIdentificationType ?? "",
        identificationNumber: nominee.nomineeIdentificationNumber ?? "",
        email: nominee.nomineeEmail ?? "",
        phoneNumber: nominee.nomineePhoneNumber ?? "",
        pincode: nominee.nomineePincode ?? "",
        address1: nominee.nomineeAddress1 ?? "",
        address2: nominee.nomineeAddress2 ?? "",
        address3: nominee.nomineeAddress3 ?? "",
        city: nominee.nomineeCity ?? "",
        country: nominee.nomineeCountry ?? "",
    });

    const [isMinor, setIsMinor] = useState<boolean>(false);
    const [isNri, setIsNri] = useState<boolean>(["india", "101"].includes(nominee.nomineeCountry?.toLowerCase() ?? "") ? false : true);
    const [isSameAddress, setIsSameAddress] = useState<boolean>(false);
    const [type, setType] = useState<string>(nominee.nomineeIdentificationType ?? "");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 600);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <APColumn mainAxisSize='max' style={{ padding: "16px", width: "100vw" }}>
            <APSizedBox height={APSpacing[5]} />
            <APImage src='/assetplus/aplight.svg' height="24px" width="180.85px" />
            <APSizedBox height={APSpacing[5]} />
            <APScrollView>
                <APForm control={control}>
                    <APColumn crossAxisAlignment='center' mainAxisAlignment='center'>
                        <APColumn crossAxisAlignment='stretch' style={{ maxWidth: "min(500px, 100%)" }} mainAxisAlignment='center' >
                            <APText variant='maintext'>Hi {clientName}, as per SEBI regulations, please update your nominee details, to avoid restrictions on new transactions. Ongoing SIPs, STPs, and SWPs will continue unaffected.</APText>
                            <APSizedBox height={APSpacing[6]} />

                            {
                                nominee?.nomineeErrorRemark &&
                                <APSectionMessage
                                    title='Nominee update failed due to following reason:'
                                    description={nominee.nomineeErrorRemark}
                                    variant='negative'
                                />
                            }

                            <APText variant='h3'>Nominee Details</APText>

                            <APFormFieldText
                                label="Nominee's Name"
                                initialValue={form.name}
                                onChanged={(val) => form.name = val}
                                validator={(val) => {
                                    if (!val) {
                                        return "Nominee name required"
                                    }

                                    if (val.length > 40) {
                                        return "Max 40 characters allowed"
                                    }

                                    return null
                                }}
                            />

                            <APFormFieldDropdown<string>
                                label="Nominee's Relationship"
                                initialValue={form.relationCode}
                                items={NomineeMasterTable.RELATIONSHIP_OPTIONS}
                                onChanged={(value) => {
                                    if (value) {
                                        form.relationCode = value
                                    }
                                }}
                                validator={(val) => {
                                    if (!val) {
                                        return "Select nominee relationship"
                                    }

                                    return null
                                }}
                            />

                            {isMobile ? (
                                <APFormFieldText
                                    label="Nominee's Date of Birth"
                                    maxLength={10}
                                    keyboardType='text'
                                    initialValue={moment(form.dob, true).isValid() ? form.dob : undefined}
                                    placeholder='DD/MM/YYYY'
                                    helperText="Enter the date in DD/MM/YYYY format"
                                    onChanged={(val) => {
                                        if (moment(val, "DD/MM/YYYY", true).isValid()) {
                                            if (moment(val, "DD/MM/YYYY").isAfter(moment().subtract(18, 'year'))) {
                                                setIsMinor(true);
                                            } else {
                                                setIsMinor(false);
                                            }
                                            form.dob = moment(val, 'DD/MM/YYYY').format('YYYY-MM-DD');
                                        }
                                    }}
                                    validator={val => {
                                        if (!val) {
                                            return "Date of Birth is required";
                                        }
                                        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                                            return "Please enter the date in DD/MM/YYYY format";
                                        }
                                        const date = moment(val, "DD/MM/YYYY", true);
                                        if (!date.isValid()) {
                                            return "Please enter a valid date";
                                        }
                                        if (date.isAfter(moment())) {
                                            return "Date of Birth cannot be in the future";
                                        }
                                        return null;
                                    }}
                                />
                            ) : (
                                <APFormFieldText
                                    label="Nominee's Date of Birth"
                                    keyboardType="date"
                                    initialValue={moment(form.dob, true).isValid() ? moment(form.dob, "DD/MM/YYYY").format("YYYY-MM-DD") : undefined}
                                    onChanged={(val) => {
                                        if (moment(val).isValid()) {
                                            if (moment(val).isAfter(moment().subtract(18, 'year'))) {
                                                setIsMinor(true)
                                            } else {
                                                setIsMinor(false)
                                            }
                                            form.dob = val
                                        }
                                    }}
                                    validator={val => {
                                        if (!val) {
                                            return "Date of Birth is required";
                                        }

                                        if (!moment(val).isValid()) {
                                            return "Invalid date format"
                                        }

                                        if (moment(val).isAfter(moment())) {
                                            return "Date of Birth cannot be in the future";
                                        }

                                        return null;
                                    }}
                                />
                            )}

                            {
                                isMinor &&
                                <APFormFieldText
                                    label='Guardian Name'
                                    initialValue={form.guardianName}
                                    onChanged={(name) => form.guardianName = name}
                                    validator={(val) => {
                                        if (!val) {
                                            return "Guardian name required"
                                        }

                                        if (val.length > 35) {
                                            return "Max 35 characters allowed"
                                        }

                                        return null
                                    }}
                                />
                            }

                            <APFormFieldCheckbox
                                label="My nominee is an NRI"
                                initialValue={isNri}
                                onChanged={(val) => {
                                    if (val) {
                                        form.identificationType = "4"
                                    }
                                    else {
                                        form.identificationType = ""
                                    }
                                    setIsNri(val)
                                }}
                            />

                            <APFormFieldDropdown
                                key={`identity-type-${isNri}`}
                                label="Nominee's Identity Type"
                                disabled={isNri}
                                items={NomineeMasterTable.IDENTITY_TYPE}
                                initialValue={isNri ? "4" : form.identificationType}
                                onChanged={(val) => {
                                    if (val) {
                                        form.identificationType = val
                                        setType(val)
                                    }
                                }}
                                validator={(val) => {
                                    if (!val) {
                                        return null
                                    }

                                    if (isNri && val !== "4") {
                                        return "Identity type must be NRI";
                                    }

                                    return null
                                }}
                            />

                            <APFormFieldText
                                key={`identity-input-${type}-${isNri}`}
                                label={getIdentityPlaceholder(type)}
                                initialValue={form.identificationNumber}
                                textAppearance='uppercase'
                                onChanged={(val) => {
                                    form.identificationNumber = val;
                                }}
                                validator={(val) => {
                                    if (!val?.trim()) {
                                        return "Identity number is required";
                                    }

                                    if (type === "1" && val === nominee?.panNumber) {
                                        return "PAN can not be same as holder"
                                    }

                                    switch (type) {
                                        case '1':
                                            return REGEX.PAN.test(val.toUpperCase()) ? null : "Enter valid PAN number";
                                        case '2':
                                            return /^\d{4}$/.test(val) ? null : "Enter last 4 digits of Aadhaar";
                                        case '3':
                                            return /^[A-Z0-9]{10,15}$/i.test(val) ? null : "Enter valid Driving License number";
                                        case '4':
                                            return /^[A-PR-WYa-pr-wy][1-9]\d\s?\d{4}[1-9]$/.test(val) || val.length >= 6 ? null : "Enter valid Passport number";
                                        default: return null
                                    }

                                }}
                            />

                            <APFormFieldText
                                label="Nominee's Email"
                                initialValue={form.email}
                                onChanged={(val) => { form.email = val.trim() }}
                                validator={(val) => {
                                    if (!val) {
                                        return "Nominee Email is required";
                                    }

                                    if (!REGEX.EMAIL.test(val)) {
                                        return "Invalid Nominee Email";
                                    }

                                    return null;
                                }}
                            />

                            <APFormFieldText
                                label="Nominee's Phone"
                                initialValue={form.phoneNumber}
                                onChanged={(val) => {
                                    form.phoneNumber = val;
                                }}
                                helperText="Enter Indian phone number"
                                validator={(val) => !REGEX.PHONE.test(val) ? "Enter valid 10-digit phone number" : null}
                            />

                            <APFormFieldCheckbox
                                label="Nominee address is same as holder"
                                initialValue={isSameAddress}
                                onChanged={(val) => {
                                    setIsSameAddress(val)
                                    if (val) {
                                        form.address1 = nominee?.permanentAddress?.line1 ?? ""
                                        form.address2 = nominee?.permanentAddress?.line2 ?? ""
                                        form.address3 = nominee?.permanentAddress?.line3 ?? ""
                                        form.pincode = nominee?.permanentAddress?.pin ?? ""
                                        form.city = nominee?.permanentAddress?.city ?? ""
                                    } else {
                                        form.address1 = "";
                                        form.address2 = "";
                                        form.address3 = "";
                                        form.pincode = "";
                                        form.city = "";
                                        form.country = "";
                                    }
                                }}
                            />

                            <Fragment key={`${isSameAddress}_address_key`}>
                                <APFormFieldText
                                    label="Address Line 1"
                                    initialValue={form.address1}
                                    onChanged={(val) => { form.address1 = val.trim() }}
                                    validator={(val) => {
                                        val = val.trim();
                                        if (!val) {
                                            return "Nominee Address1 is required";
                                        }
                                        if (val.length > 40) {
                                            return "Max 40 Characters";
                                        }
                                        return null;
                                    }}
                                />

                                <APFormFieldText
                                    label="Address Line 2 (Optional)"
                                    initialValue={form.address2}
                                    onChanged={(val) => {
                                        form.address2 = val.trim();
                                    }}
                                    validator={(val) => val.trim()?.length > 40 ? "Max 40 characters" : null}
                                />


                                <APFormFieldText
                                    label="Address Line 3 (Optional)"
                                    initialValue={form.address3}
                                    onChanged={(val) => {
                                        form.address3 = val.trim()
                                    }}
                                    validator={(val) => val.trim()?.length > 40 ? "Max 40 characters" : null}
                                />

                                <APFormFieldText
                                    label="Pincode"
                                    initialValue={form.pincode}
                                    onChanged={async (val) => {
                                        form.pincode = val;
                                    }}
                                    validator={(val) => {
                                        if (!isNri && !REGEX.PIN.test(val)) {
                                            return "Invalid Pincode";
                                        }
                                        return null;
                                    }}
                                />

                                <APFormFieldText
                                    label="City"
                                    initialValue={form.city}
                                    onChanged={(val) => {
                                        form.city = val;
                                    }}
                                    validator={(val) => {
                                        if (!val.trim()) {
                                            return "City is required"
                                        }

                                        if (val.length > 35) {
                                            return "Max 35 characters allowed"
                                        }

                                        if (!/^[A-Za-z\s]+$/.test(val)) {
                                            return "Invalid city"
                                        }

                                        return null
                                    }}
                                />

                                <APFormFieldDropdown
                                    label="Country"
                                    initialValue={form.country}
                                    items={NomineeMasterTable.COUNTRIES.map(c => ({ label: c.label ?? "", value: c.code }))}
                                    onChanged={(val) => {
                                        if (val) {
                                            form.country = val
                                        }
                                    }}
                                    validator={(val) => {
                                        if (!val) {
                                            return "Country is required";
                                        }
                                        if (val.length > 35) {
                                            return "Max 35 characters";
                                        }
                                        const country = val.toLowerCase();
                                        if (isNri) {
                                            if (country === "101") {
                                                return "NRI cannot have country as India";
                                            }
                                        } else {
                                            if (country !== "101") {
                                                return "Non-NRI nominee must have India as country";
                                            }
                                        }
                                        return null;
                                    }}
                                />
                            </Fragment>

                            <APFormFieldCheckbox
                                label="Include nominee details in all my statements of account (SOA)"
                                initialValue={form.showInSoa}
                                onChanged={(val) => form.showInSoa = val}
                            />

                            <Divider />
                            <APRow mainAxisAlignment='center'>
                                <APFormSubmit
                                    type='primary'
                                    size='medium'
                                    onClick={async () => {
                                        if (await control.validate()) {
                                            try {
                                                if (!props.token) {
                                                    throw new Error("Unauthorized Access")
                                                }

                                                if (isNri) {
                                                    form.identificationType = "4"
                                                }
                                                await getIt(MutualFundApiServices).updateNominee(form, props.token);
                                                window.location.href = "/nominee_regulatory_validation/successPage";
                                            } catch (error) {
                                                handleErrorMessage(error)
                                            }
                                        }
                                    }}
                                >
                                    Next
                                </APFormSubmit>
                            </APRow>
                        </APColumn>
                    </APColumn>
                </APForm>
            </APScrollView>
        </APColumn >
    );
}

export class NRVNomineeValidationModel {
    name!: string;
    relationship!: string;
    relationshipCode!: string;
}

export class NRVGuardianValidationModel {
    name!: string;
    relationshipCode!: string;
}
